import {useEffect, useMemo, useState} from 'react'
import get from 'lodash/get'
import noop from 'lodash/noop'

import {auth} from 'configs/firebase'
import useUserContext from 'modules/auth/hooks/useUserContext'
import {getIdToken} from 'modules/auth/utils/userUtils'

import {createSession, deleteSession} from './api'

export const SESSION_STATE = {
  created: 'created',
  deleted: 'deleted',
  error: 'error',
}

// refresh every 15 seconds
const REFRESH_INTERVAL = 1000 * 15

export default function useSession() {
  const {
    error: userLoadError,
    loading: userIsLoading,
    auth: user,
  } = useUserContext()
  const [apiError, setApiError] = useState(false)
  const [apiIsLoading, setApiLoading] = useState(false)
  const [state, setState] = useState(null)
  const [refreshFn, setRefreshFn] = useState(noop)

  useEffect(() => {
    async function createOrDeleteSession() {
      if (userIsLoading || userLoadError) return

      setApiLoading(true)
      let result = {}
      let state = null
      try {
        if (!user) {
          result = await deleteSession()
          state = SESSION_STATE.deleted
        } else {
          result = await createSession(await getIdToken())
          state = SESSION_STATE.created
        }
      } catch (err) {
        state = SESSION_STATE.error
        console.warn(err)
      }
      setApiLoading(false)

      if (get(result, 'status') === 'ok') {
        setApiError(false)
      } else {
        setApiError(true)
      }

      setState(state)
    }

    const intervalId = setInterval(createOrDeleteSession, REFRESH_INTERVAL)
    const listener = auth.onIdTokenChanged(createOrDeleteSession)

    setRefreshFn(() => createOrDeleteSession)

    return () => {
      clearInterval(intervalId)
      listener()
    }
  }, [userLoadError, userIsLoading, user])

  return useMemo(
    () => ({
      error: userLoadError || apiError,
      loading: userIsLoading || apiIsLoading,
      state,
      refresh: refreshFn,
    }),
    [userLoadError, apiError, userIsLoading, apiIsLoading, state, refreshFn]
  )
}
